import queryString from "query-string";

import { INTEGRATION_CREDENTIAL } from "constants/actionTypes";
import { XeroSyncDatapoint } from "constants/integrations";

import { serializeIntegrationBusinessCreate } from "lib/serializers/integrationBusinesses";
import { serializeIntegrationCredential } from "lib/serializers/integrationCredentials";

import { URLCreatorProps } from "./types";
import {
  offlineActionCreator,
  offlineSubActionCreator,
  urlCheckAndAddIdAndAction,
} from "./lib";

interface IntegrationCredentialActionProps {
  id: string;
  integrationCredentialId?: string;
}

interface SynchronizeActionProps extends IntegrationCredentialActionProps {
  action: "sync";
  dataPoint?: XeroSyncDatapoint;
}

interface PushLedgerAccountsActionProps
  extends IntegrationCredentialActionProps {
  action: "push-ledger-accounts";
  ledgerAccountIds: string;
}

interface SyncDocumentsActionProps extends IntegrationCredentialActionProps {
  action: "sync-documents";
}

interface CreateBusinessActionProps extends IntegrationCredentialActionProps {
  action: "create-business";
  business_id: string;
  email_address: string;
  abn?: string | null;
  account_number?: string | null;
  bsb?: string | null;
  addresses?: Record<string, unknown>[];
  names?: Record<string, unknown>[];
  phones?: Record<string, unknown>[];
}

export type XeroActionProps =
  | SynchronizeActionProps
  | PushLedgerAccountsActionProps
  | SyncDocumentsActionProps
  | CreateBusinessActionProps;

export interface CheckXeroActiveAction {
  type: string;
  nextFunction: (props: XeroActionProps) => { type: string };
  props: XeroActionProps;
}

export interface ConnectIntegrationAction {
  type: string;
  options: {
    redirectToActivate?: boolean;
  };
  payload: {
    id: string;
  };
}

export interface ActivateIntegrationAction {
  type: string;
  id: string;
}

const urlCreator = ({
  changesSince,
  id,
  action,
  dataPoint,
}: URLCreatorProps = {}) =>
  queryString.stringifyUrl({
    url: urlCheckAndAddIdAndAction("/v2/integration-credentials/", id, action),
    query: {
      changesSince,
      data_point: dataPoint,
    },
  });

export const IntegrationCredentialAction = offlineActionCreator(
  INTEGRATION_CREDENTIAL,
  serializeIntegrationCredential,
  urlCreator,
  false,
);

IntegrationCredentialAction.activate = (
  integrationCredentialId: string,
  tenantId: string,
) => ({
  type: INTEGRATION_CREDENTIAL.ACTIVATE.REQUEST,
  id: integrationCredentialId,
  tenantId,
});

IntegrationCredentialAction.synchronize = (
  integrationCredentialId: string,
  dataPoint?: XeroSyncDatapoint,
) =>
  IntegrationCredentialAction.action(
    INTEGRATION_CREDENTIAL.SYNCHRONIZE,
    offlineSubActionCreator(INTEGRATION_CREDENTIAL.SYNCHRONIZE, urlCreator),
    {
      id: integrationCredentialId,
      action: "sync",
      dataPoint,
    },
  );

interface PushLedgerAccountUrlCreatorProps extends URLCreatorProps {
  ledgerAccountIds?: string;
}

const pushLedgerAccountUrlCreator = ({
  changesSince,
  id,
  action,
  ledgerAccountIds,
}: PushLedgerAccountUrlCreatorProps = {}) =>
  queryString.stringifyUrl({
    url: urlCheckAndAddIdAndAction("/v2/integration-credentials/", id, action),
    query: {
      changesSince,
      ledger_account_ids: ledgerAccountIds,
    },
  });

IntegrationCredentialAction.pushLedgerAccount = (
  integrationCredentialId: string,
  ledgerAccountIds: string,
) => {
  return IntegrationCredentialAction.action(
    INTEGRATION_CREDENTIAL.PUSH_LEDGER_ACCOUNTS,
    offlineSubActionCreator(
      INTEGRATION_CREDENTIAL.PUSH_LEDGER_ACCOUNTS,
      pushLedgerAccountUrlCreator,
    ),
    {
      id: integrationCredentialId,
      action: "push-ledger-accounts",
      ledgerAccountIds,
    },
  );
};

IntegrationCredentialAction.syncDocuments = (
  integrationCredentialId: string,
  ignoredOptions?: unknown,
) =>
  IntegrationCredentialAction.action(
    INTEGRATION_CREDENTIAL.SYNC_DOCUMENTS,
    offlineSubActionCreator(INTEGRATION_CREDENTIAL.SYNC_DOCUMENTS, urlCreator),
    {
      id: integrationCredentialId,
      action: "sync-documents",
    },
  );

IntegrationCredentialAction.createBusiness = (
  integrationCredentialId: string,
  deserializedPayload: unknown,
) => {
  const payload = serializeIntegrationBusinessCreate(deserializedPayload);
  return IntegrationCredentialAction.action(
    INTEGRATION_CREDENTIAL.CREATE_BUSINESS,
    offlineSubActionCreator(
      INTEGRATION_CREDENTIAL.CREATE_BUSINESS,
      urlCreator,
      "POST",
    ),
    {
      id: integrationCredentialId,
      action: "create-business",
      ...payload,
    },
  );
};
